import { ReactNode } from "react";
import { ViewItem } from "../../../../Components/Views/ViewItem";
import { controllers } from "../../../../Controllers/Controllers";
import { LeadCategoryType } from "../../../../DataTypes/leads";
import '../outreach-styles.css'
import './categories-styles.css'

type CategoriesPageState = {
    categories: LeadCategoryType[];
    loading: boolean;
    error: string | null;
    needsAuth: boolean;
    modalOpen: boolean;
    editingCategory: LeadCategoryType | null;
    formName: string;
    formDescription: string;
    formActive: boolean;
    saving: boolean;
    formError: string | null;
    deletingId: number | null;
}

class CategoriesPage extends ViewItem {
    state: CategoriesPageState;

    constructor(props: any) {
        super(props)
        this.state = {
            categories: [],
            loading: true,
            error: null,
            needsAuth: false,
            modalOpen: false,
            editingCategory: null,
            formName: '',
            formDescription: '',
            formActive: true,
            saving: false,
            formError: null,
            deletingId: null,
        }
    }

    componentDidMount(): void {
        this.loadCategories()
    }

    loadCategories = async () => {
        this.setState({ loading: true, error: null, needsAuth: false })
        try {
            if (!controllers().auth.userToken) {
                this.setState({ loading: false, needsAuth: true })
                return
            }
            const response = await controllers().outreach.getCategories(true)
            if (!response || response.statusCode !== 200) {
                this.setState({ loading: false, error: response?.error || 'Failed to load categories.' })
                return
            }
            this.setState({ categories: response.data?.categories || [], loading: false })
        } catch (e) {
            this.setState({ loading: false, error: 'Failed to load categories.' })
        }
    }

    openAddModal = () => {
        this.setState({
            modalOpen: true,
            editingCategory: null,
            formName: '',
            formDescription: '',
            formActive: true,
            formError: null,
        })
    }

    openEditModal = (category: LeadCategoryType) => {
        this.setState({
            modalOpen: true,
            editingCategory: category,
            formName: category.name,
            formDescription: category.description,
            formActive: category.is_active,
            formError: null,
        })
    }

    closeModal = () => {
        if (this.state.saving) return
        this.setState({ modalOpen: false })
    }

    handleSave = async () => {
        const { editingCategory, formName, formDescription, formActive } = this.state
        if (!formName.trim()) {
            this.setState({ formError: 'Name is required' })
            return
        }
        this.setState({ saving: true, formError: null })

        const fields = { name: formName.trim(), description: formDescription, is_active: formActive }
        const response = editingCategory
            ? await controllers().outreach.editCategory(editingCategory.id, fields)
            : await controllers().outreach.addCategory(fields)

        if (!response || response.statusCode !== 200) {
            this.setState({ saving: false, formError: response?.error || 'Failed to save category.' })
            return
        }

        this.setState({ saving: false, modalOpen: false })
        this.loadCategories()
    }

    handleDelete = async (category: LeadCategoryType) => {
        const confirmed = window.confirm(
            `Delete category "${category.name}"? Leads using it will just lose their category, not get deleted.`
        )
        if (!confirmed) return

        this.setState({ deletingId: category.id })
        const response = await controllers().outreach.deleteCategory(category.id)
        this.setState({ deletingId: null })
        if (response && response.statusCode === 200) {
            this.loadCategories()
        }
    }

    renderModal = () => {
        const { modalOpen, editingCategory, formName, formDescription, formActive, saving, formError } = this.state
        if (!modalOpen) return null
        return (
            <div className="outreach-modal-overlay" onClick={this.closeModal}>
                <div className="outreach-modal" onClick={(e) => e.stopPropagation()}>
                    <h3 className="outreach-modal-title">
                        {editingCategory ? 'Edit Category' : 'New Category'}
                    </h3>
                    <p className="outreach-modal-subtitle">
                        {editingCategory ? 'Update this category.' : 'Create a new lead category.'}
                    </p>

                    {formError && <div className="category-form-error">{formError}</div>}

                    <div className="category-form-group">
                        <label className="category-form-label">Name</label>
                        <input
                            type="text"
                            className="category-form-input"
                            value={formName}
                            onChange={(e) => this.setState({ formName: e.target.value })}
                            placeholder="e.g. AI Automation"
                            disabled={saving}
                            autoFocus
                        />
                    </div>

                    <div className="category-form-group">
                        <label className="category-form-label">Description</label>
                        <textarea
                            className="category-form-textarea"
                            value={formDescription}
                            onChange={(e) => this.setState({ formDescription: e.target.value })}
                            placeholder="Optional"
                            disabled={saving}
                            rows={3}
                        />
                    </div>

                    <label className="category-form-checkbox-row">
                        <input
                            type="checkbox"
                            checked={formActive}
                            onChange={(e) => this.setState({ formActive: e.target.checked })}
                            disabled={saving}
                        />
                        Active
                    </label>

                    <div className="category-form-actions">
                        <button className="outreach-modal-cancel" onClick={this.closeModal} disabled={saving}>
                            Cancel
                        </button>
                        <button className="outreach-add-btn" onClick={this.handleSave} disabled={saving}>
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    renderTable = () => {
        const { categories, deletingId } = this.state
        return (
            <div className="outreach-table-wrap">
                <table className="outreach-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Description</th>
                            <th>Leads</th>
                            <th>Status</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {categories.map(category => (
                            <tr key={category.id}>
                                <td className="category-name-cell">{category.name}</td>
                                <td className="outreach-muted">{category.description || '—'}</td>
                                <td className="outreach-muted">{category.lead_count ?? 0}</td>
                                <td>
                                    <span className={`category-status ${category.is_active ? 'category-status-active' : 'category-status-inactive'}`}>
                                        {category.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td>
                                    <div className="category-row-actions">
                                        <button className="outreach-edit-btn" onClick={() => this.openEditModal(category)}>
                                            Edit
                                        </button>
                                        <button
                                            className="outreach-edit-btn category-delete-btn"
                                            onClick={() => this.handleDelete(category)}
                                            disabled={deletingId === category.id}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )
    }

    render(): ReactNode {
        const { categories, loading, error, needsAuth } = this.state

        return (
            <div className="outreach-page">
                <div className="outreach-header">
                    <div className="outreach-title-header">
                        <h1 className="outreach-title">Lead Categories</h1>
                        <p className="outreach-subtitle">
                            {categories.length} categor{categories.length === 1 ? 'y' : 'ies'}
                        </p>
                    </div>
                    <button className="outreach-add-btn" onClick={this.openAddModal}>
                        + Add Category
                    </button>
                </div>

                {!loading && needsAuth && (
                    <div className="outreach-banner outreach-banner-info">
                        <span className="outreach-banner-text">Sign in to manage categories.</span>
                        <button className="outreach-banner-action" onClick={() => controllers().modals.authModal.show()}>
                            Sign In
                        </button>
                    </div>
                )}

                {!loading && error && (
                    <div className="outreach-banner outreach-banner-error">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        <span className="outreach-banner-text">{error}</span>
                        <button className="outreach-banner-action" onClick={this.loadCategories}>
                            Retry
                        </button>
                    </div>
                )}

                {loading && (
                    <div className="outreach-state">
                        <div className="outreach-spinner" />
                    </div>
                )}

                {!loading && !error && !needsAuth && categories.length === 0 && (
                    <div className="outreach-state outreach-empty">
                        No categories yet.
                    </div>
                )}

                {!loading && !error && !needsAuth && categories.length > 0 && this.renderTable()}

                {this.renderModal()}
            </div>
        )
    }
}

export { CategoriesPage }
